if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.imageZoomState = new WeakMap();
      }

      hide() {
        this.resetAllImageZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        if (!this.openedBy) return;
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.resetAllImageZoom();
        this.enableMobileImageZoom(activeMedia);
      }

      enableMobileImageZoom(activeMedia) {
        if (!this.mobileMediaQuery.matches) return;

        const image = activeMedia.matches('img') ? activeMedia : activeMedia.querySelector('img');
        if (!image) return;
        if (image.dataset.mobileZoomInitialized === 'true') return;
        image.dataset.mobileZoomInitialized = 'true';

        const state = {
          scale: 1,
          translateX: 0,
          translateY: 0,
          pinchStartDistance: 0,
          pinchStartScale: 1,
          panStartX: 0,
          panStartY: 0,
          panStartTranslateX: 0,
          panStartTranslateY: 0,
          lastTapAt: 0,
        };
        this.imageZoomState.set(image, state);

        image.addEventListener('touchstart', (event) => this.onImageTouchStart(event, image), { passive: false });
        image.addEventListener('touchmove', (event) => this.onImageTouchMove(event, image), { passive: false });
        image.addEventListener('touchend', (event) => this.onImageTouchEnd(event, image));
        image.addEventListener('touchcancel', () => this.resetImagePanState(image));
      }

      onImageTouchStart(event, image) {
        const state = this.imageZoomState.get(image);
        if (!state) return;

        if (event.touches.length === 2) {
          state.pinchStartDistance = this.getTouchDistance(event.touches);
          state.pinchStartScale = state.scale;
          event.preventDefault();
          return;
        }

        if (event.touches.length !== 1) return;

        const touch = event.touches[0];
        state.panStartX = touch.clientX;
        state.panStartY = touch.clientY;
        state.panStartTranslateX = state.translateX;
        state.panStartTranslateY = state.translateY;

        const now = Date.now();
        if (now - state.lastTapAt < 280) {
          event.preventDefault();
          const nextScale = state.scale > 1 ? 1 : 2;
          this.zoomAtPoint(image, nextScale, touch.clientX, touch.clientY);
          state.lastTapAt = 0;
          return;
        }
        state.lastTapAt = now;
      }

      onImageTouchMove(event, image) {
        const state = this.imageZoomState.get(image);
        if (!state) return;

        if (event.touches.length === 2) {
          const distance = this.getTouchDistance(event.touches);
          if (!state.pinchStartDistance) {
            state.pinchStartDistance = distance;
            state.pinchStartScale = state.scale;
          }
          const scaleRatio = distance / state.pinchStartDistance;
          state.scale = this.clamp(state.pinchStartScale * scaleRatio, 1, 4);
          this.applyImageTransform(image);
          event.preventDefault();
          return;
        }

        if (event.touches.length !== 1 || state.scale <= 1) return;

        const touch = event.touches[0];
        const dx = touch.clientX - state.panStartX;
        const dy = touch.clientY - state.panStartY;
        state.translateX = state.panStartTranslateX + dx;
        state.translateY = state.panStartTranslateY + dy;
        this.applyImageTransform(image);
        event.preventDefault();
      }

      onImageTouchEnd(event, image) {
        const state = this.imageZoomState.get(image);
        if (!state) return;

        if (event.touches.length < 2) {
          state.pinchStartDistance = 0;
          state.pinchStartScale = state.scale;
        }
        if (state.scale <= 1.01) {
          this.resetImageZoom(image);
        }
      }

      zoomAtPoint(image, targetScale, pointX, pointY) {
        const state = this.imageZoomState.get(image);
        if (!state) return;

        const rect = image.getBoundingClientRect();
        const offsetX = pointX - (rect.left + rect.width / 2);
        const offsetY = pointY - (rect.top + rect.height / 2);

        state.scale = this.clamp(targetScale, 1, 4);
        if (state.scale <= 1) {
          state.translateX = 0;
          state.translateY = 0;
        } else {
          state.translateX = -offsetX * (state.scale - 1);
          state.translateY = -offsetY * (state.scale - 1);
        }
        this.applyImageTransform(image);
      }

      applyImageTransform(image) {
        const state = this.imageZoomState.get(image);
        if (!state) return;

        const maxTranslateX = ((state.scale - 1) * image.clientWidth) / 2;
        const maxTranslateY = ((state.scale - 1) * image.clientHeight) / 2;
        state.translateX = this.clamp(state.translateX, -maxTranslateX, maxTranslateX);
        state.translateY = this.clamp(state.translateY, -maxTranslateY, maxTranslateY);

        image.style.transform = `translate3d(${state.translateX}px, ${state.translateY}px, 0) scale(${state.scale})`;
        image.style.touchAction = state.scale > 1 ? 'none' : 'pan-y';
        image.classList.toggle('is-zoomed', state.scale > 1);
      }

      resetImageZoom(image) {
        const state = this.imageZoomState.get(image);
        if (!state) return;

        state.scale = 1;
        state.translateX = 0;
        state.translateY = 0;
        state.pinchStartDistance = 0;
        state.pinchStartScale = 1;
        image.style.transform = '';
        image.style.touchAction = 'pan-y';
        image.classList.remove('is-zoomed');
      }

      resetImagePanState(image) {
        const state = this.imageZoomState.get(image);
        if (!state) return;
        state.pinchStartDistance = 0;
        state.pinchStartScale = state.scale;
      }

      resetAllImageZoom() {
        this.querySelectorAll('.product-media-modal__content img').forEach((image) => this.resetImageZoom(image));
      }

      getTouchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.hypot(dx, dy);
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }
    }
  );
}
